const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  // Some in-app browser WebViews (e.g. Instagram's) are known to delay or
  // skip later IntersectionObserver callbacks — most often after the tab
  // is backgrounded/resized mid-session — which would otherwise leave a
  // section stuck invisible (`scroll-trigger--offscreen`) forever once a
  // user scrolls past it without the reveal ever firing. If the API isn't
  // available at all, skip straight to the safety net below instead of
  // throwing and leaving every trigger element unobserved.
  try {
    const observer = new IntersectionObserver(onIntersection, {
      rootMargin: '0px 0px -50px 0px',
    });
    animationTriggerElements.forEach((element) => observer.observe(element));
  } catch (error) {
    console.error(error);
  }

  watchForStuckScrollTriggers();
}

// Safety net for the failure mode above: periodically checks for any
// still-offscreen trigger element that's actually within (or has passed
// through) the viewport and reveals it directly via getBoundingClientRect,
// independent of whether the IntersectionObserver ever fired for it. Runs
// a few times then stops — cheap, and never overrides a working observer
// since revealed elements are simply skipped on the next pass.
let stuckScrollTriggerWatcher = null;
function watchForStuckScrollTriggers() {
  if (stuckScrollTriggerWatcher) return;

  let checksRemaining = 20; // ~10s at the interval below, generous for slow WebViews
  stuckScrollTriggerWatcher = setInterval(() => {
    checksRemaining -= 1;
    const stillOffscreen = document.getElementsByClassName(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

    if (stillOffscreen.length === 0 || checksRemaining <= 0) {
      clearInterval(stuckScrollTriggerWatcher);
      stuckScrollTriggerWatcher = null;
      return;
    }

    const viewportHeight = window.innerHeight;
    Array.from(stillOffscreen).forEach((element) => {
      // Only reveal elements that are genuinely on-screen right now — the
      // same condition the IntersectionObserver itself would already have
      // matched. This is strictly a backstop for a callback that failed to
      // fire, not an early trigger, so normal reveal timing/animation is
      // unaffected whenever the observer is working correctly.
      const rect = element.getBoundingClientRect();
      const isCurrentlyVisible = rect.top < viewportHeight && rect.bottom > 0;
      if (isCurrentlyVisible) element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
  }, 500);
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
